import { MAX_UPLOAD_BYTES } from '../uploadLimits';

/**
 * Form Validation Utility
 * ========================
 * Provides comprehensive field-level validation for various data types.
 * Based on backend validation patterns, adapted for frontend TypeScript.
 */

// Email regex (RFC 5322 simplified)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// URL regex
const URL_REGEX = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*)$/;

// Phone number regex (international format support)
const PHONE_REGEX = /^\+?1?\d{9,15}$/;

// UUID regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Field-type specific patterns
// Use a BLOCKLIST approach: block only control characters, allow all Unicode text
// This supports Nordic (å,ä,ö), Icelandic (þ,ð), Cyrillic, Baltic, Arabic,
// CJK, and any other legitimate international characters.
const FIELD_PATTERNS = {
  title: /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]{1,255}$/u,
  text: /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]{1,255}$/u,
  alphanumeric_space: /^[\p{L}\p{N}\s]+$/u,  // Unicode letters + numbers + spaces
};

// Emoji and special symbol detection patterns
// Unicode emoji detection (covers most emoji ranges)
const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;

// Non-ASCII special characters detection (symbols, decorative characters)
// IMPORTANT: This EXCLUDES Unicode letters (\p{L}) and marks (\p{M}) so that
// international characters (å, ä, ö, é, ñ, Cyrillic, Arabic, CJK, etc.) are NOT flagged.
// Only flags non-letter, non-digit, non-punctuation, non-ASCII symbols.
const SPECIAL_SYMBOLS_REGEX = /[^\p{L}\p{M}\p{N}\p{P}\p{Z}\p{S}\x20-\x7E]/gu;

// Combined emoji and problematic special symbols using alternation
const DISALLOWED_CHARACTERS_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]|[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/gu;

/**
 * Validate email address format
 */
export const validateEmail = (email: string): boolean => {
  if (typeof email !== 'string' || email.length > 255) {
    return false;
  }
  return EMAIL_REGEX.test(email.toLowerCase());
};

/**
 * Validate URL format and protocol
 */
export const validateUrl = (url: string): boolean => {
  if (typeof url !== 'string' || url.length > 2048) {
    return false;
  }
  try {
    const urlObj = new URL(url);
    return ['http', 'https'].includes(urlObj.protocol.replace(':', ''));
  } catch {
    return false;
  }
};

/**
 * Validate phone number format
 */
export const validatePhone = (phone: string): boolean => {
  if (typeof phone !== 'string') {
    return false;
  }
  // Remove common phone formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
  return PHONE_REGEX.test(cleaned);
};

/**
 * Validate UUID format
 */
export const validateUUID = (uuid: string): boolean => {
  if (typeof uuid !== 'string') {
    return false;
  }
  return UUID_REGEX.test(uuid);
};

/**
 * Validate integer value with optional range checking
 */
export const validateInteger = (
  value: any,
  minVal?: number,
  maxVal?: number
): boolean => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return false;
  }
  try {
    const intVal = parseInt(String(value), 10);
    if (Number.isNaN(intVal)) {
      return false;
    }
    if (minVal !== undefined && intVal < minVal) {
      return false;
    }
    if (maxVal !== undefined && intVal > maxVal) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate boolean value
 */
export const validateBoolean = (value: any): boolean => {
  if (typeof value === 'boolean') {
    return true;
  }
  if (typeof value === 'string') {
    return ['true', 'false', '1', '0', 'yes', 'no'].includes(value.toLowerCase());
  }
  if (typeof value === 'number') {
    return [0, 1].includes(value);
  }
  return false;
};

/**
 * Validate string field length
 */
export const validateStringLength = (
  value: string,
  minLength: number = 1,
  maxLength: number = 255
): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return trimmed.length >= minLength && trimmed.length <= maxLength;
};

/**
 * Validate date string with specified format
 */
export const validateDate = (
  dateStr: string,
  format: string = 'YYYY-MM-DD'
): boolean => {
  if (typeof dateStr !== 'string') {
    return false;
  }
  try {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};

/**
 * Validate ISO 8601 datetime string
 */
export const validateISODateTime = (datetimeStr: string): boolean => {
  if (typeof datetimeStr !== 'string') {
    return false;
  }
  try {
    const date = new Date(datetimeStr);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};

/**
 * Validate value is in allowed choices (dropdown validation)
 */
export const validateChoice = (value: any, choices: any[]): boolean => {
  if (!Array.isArray(choices) || choices.length === 0) {
    return false;
  }
  return choices.some(choice => {
    if (typeof choice === 'object' && choice.value !== undefined) {
      return choice.value === value || String(choice.value) === String(value);
    }
    return choice === value || String(choice) === String(value);
  });
};

/**
 * Validate file upload
 */
export const validateFileUpload = (
  file: File,
  maxSizeBytes: number = MAX_UPLOAD_BYTES,
  allowedExtensions?: string[]
): { isValid: boolean; error?: string } => {
  if (!(file instanceof File)) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > maxSizeBytes) {
    const maxMB = (maxSizeBytes / 1024 / 1024).toFixed(1);
    return {
      isValid: false,
      error: `File size exceeds maximum of ${maxMB}MB`,
    };
  }

  if (file.size === 0) {
    return { isValid: false, error: 'File is empty' };
  }

  // Check file extension if specified
  if (allowedExtensions && allowedExtensions.length > 0) {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!allowedExtensions.includes(ext)) {
      return {
        isValid: false,
        error: `File type not allowed. Allowed types: ${allowedExtensions.join(', ')}`,
      };
    }
  }

  return { isValid: true };
};

/**
 * Detect emojis in text
 */
export const containsEmoji = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  return EMOJI_REGEX.test(value);
};

/**
 * Detect special non-ASCII symbols that may be problematic.
 * Does NOT flag international letters (å, ä, ö, é, ñ, Cyrillic, Arabic, CJK, etc.)
 * Only flags control characters and truly problematic non-text symbols.
 */
export const containsSpecialSymbols = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  // Only flag control characters (not international letters)
  // Control chars: \x00-\x08, \x0B, \x0C, \x0E-\x1F, \x7F-\x9F
  return /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/u.test(value);
};

/**
 * Detect disallowed characters (emojis, control characters, etc.)
 */
export const containsDisallowedCharacters = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  return DISALLOWED_CHARACTERS_REGEX.test(value);
};

/**
 * Detect potential SQL injection patterns (basic detection)
 */
export const detectSQLInjection = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }

  const patterns = [
    /('\s*(OR|AND)\s*')/i,
    /(\bUNION\b.*\bSELECT\b)/i,
    /(\bDROP\b|\bDELETE\b|\bINSERT\b|\bUPDATE\b)/i,
    /(;\s*DROP|\bXP_)/i,
  ];

  return patterns.some(pattern => pattern.test(value));
};

/**
 * Validate JSON string
 */
export const validateJSON = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Generic validation function that handles multiple validators
 */
export const runValidator = (
  value: any,
  validatorType: string,
  options?: any
): { isValid: boolean; error?: string } => {
  if (value === null || value === undefined || value === '') {
    return { isValid: true }; // Allow empty for optional fields
  }

  switch (validatorType) {
    case 'email':
      return {
        isValid: validateEmail(String(value)),
        error: !validateEmail(String(value)) ? 'Invalid email format' : undefined,
      };

    case 'phone':
      return {
        isValid: validatePhone(String(value)),
        error: !validatePhone(String(value))
          ? 'Invalid phone number format (e.g., +1234567890)'
          : undefined,
      };

    case 'url':
      return {
        isValid: validateUrl(String(value)),
        error: !validateUrl(String(value))
          ? 'Invalid URL format (must start with http:// or https://)'
          : undefined,
      };

    case 'uuid':
      return {
        isValid: validateUUID(String(value)),
        error: !validateUUID(String(value)) ? 'Invalid UUID format' : undefined,
      };

    case 'integer':
      return {
        isValid: validateInteger(value, options?.min, options?.max),
        error: !validateInteger(value, options?.min, options?.max)
          ? `Invalid integer value${
              options?.min !== undefined && options?.max !== undefined
                ? ` (must be between ${options.min} and ${options.max})`
                : ''
            }`
          : undefined,
      };

    case 'boolean':
      return {
        isValid: validateBoolean(value),
        error: !validateBoolean(value) ? 'Invalid boolean value' : undefined,
      };

    case 'title':
    case 'text':
      return {
        isValid: validateStringLength(
          String(value),
          options?.minLength || 1,
          options?.maxLength || 255
        ),
        error: !validateStringLength(String(value), options?.minLength || 1, options?.maxLength || 255)
          ? `Length must be between ${options?.minLength || 1} and ${options?.maxLength || 255} characters`
          : undefined,
      };

    case 'body':
      return {
        isValid: validateStringLength(
          String(value),
          options?.minLength || 1,
          options?.maxLength || 5000
        ),
        error: !validateStringLength(String(value), options?.minLength || 1, options?.maxLength || 5000)
          ? `Length must be between ${options?.minLength || 1} and ${options?.maxLength || 5000} characters`
          : undefined,
      };

    case 'dropdown':
      return {
        isValid: validateChoice(value, options?.choices || []),
        error: !validateChoice(value, options?.choices || [])
          ? `Invalid choice. Allowed values: ${(options?.choices || []).join(', ')}`
          : undefined,
      };

    case 'file':
      return validateFileUpload(value, options?.maxSize, options?.extensions);

    case 'date':
      return {
        isValid: validateDate(String(value), options?.format),
        error: !validateDate(String(value), options?.format)
          ? `Invalid date format (expected ${options?.format || 'YYYY-MM-DD'})`
          : undefined,
      };

    case 'iso_datetime':
      return {
        isValid: validateISODateTime(String(value)),
        error: !validateISODateTime(String(value))
          ? 'Invalid ISO 8601 datetime format'
          : undefined,
      };

    case 'no_emoji':
      return {
        isValid: !containsEmoji(String(value)),
        error: containsEmoji(String(value))
          ? 'Emojis are not allowed'
          : undefined,
      };

    case 'no_special_symbols':
      return {
        isValid: !containsSpecialSymbols(String(value)),
        error: containsSpecialSymbols(String(value))
          ? 'Special symbols are not allowed'
          : undefined,
      };

    case 'no_disallowed_chars':
      return {
        isValid: !containsDisallowedCharacters(String(value)),
        error: containsDisallowedCharacters(String(value))
          ? 'Invalid characters detected'
          : undefined,
      };

    default:
      return { isValid: false, error: `Unknown validator type: ${validatorType}` };
  }
};
